import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CalendarDays, Download } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import type { Therapist, RevenueStats, TranslateFn } from '../../types';

const API_URL = process.env.REACT_APP_BACKEND_URL;

interface RevenueTabProps {
  revenuePeriod: string;
  setRevenuePeriod: (period: string) => void;
  revenueStartDate: string | null;
  setRevenueStartDate: (date: string | null) => void;
  revenueEndDate: string | null;
  setRevenueEndDate: (date: string | null) => void;
  revenueTherapistFilter: string;
  setRevenueTherapistFilter: (filter: string) => void;
  currentRevenueData: RevenueStats | null;
  therapists: Therapist[];
  token: string;
  setShowDatePicker: (show: boolean) => void;
  setDatePickerMode: (mode: 'start' | 'end') => void;
}

export const RevenueTab = ({
  revenuePeriod, setRevenuePeriod,
  revenueStartDate, setRevenueStartDate,
  revenueEndDate, setRevenueEndDate,
  revenueTherapistFilter, setRevenueTherapistFilter,
  currentRevenueData, therapists, token,
  setShowDatePicker, setDatePickerMode
}: RevenueTabProps) => {
  const { t } = useLanguage();
  const authHeaders = { Authorization: `Bearer ${token}` };

  const handleExportCSV = async () => {
    const params = new URLSearchParams({ period: revenuePeriod, therapist: revenueTherapistFilter });
    if (revenueStartDate) params.append('start_date', revenueStartDate);
    if (revenueEndDate) params.append('end_date', revenueEndDate);
    
    const response = await fetch(`${API_URL}/api/admin/export/revenue/csv?${params}`, { headers: authHeaders });
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Angel_Massage_Revenue_${revenuePeriod}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = async () => {
    const params = new URLSearchParams({ period: revenuePeriod, therapist: revenueTherapistFilter });
    if (revenueStartDate) params.append('start_date', revenueStartDate);
    if (revenueEndDate) params.append('end_date', revenueEndDate);
    
    const response = await fetch(`${API_URL}/api/admin/export/revenue/pdf?${params}`, { headers: authHeaders });
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Angel_Massage_Revenue_${revenuePeriod}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>{t('admin.reportPeriod')}</Label>
              <Select value={revenuePeriod} onValueChange={(val) => {
                setRevenuePeriod(val);
                if (val !== 'custom') { setRevenueStartDate(null); setRevenueEndDate(null); }
              }}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">{t('admin.last7Days')}</SelectItem>
                  <SelectItem value="monthly">{t('admin.last30Days')}</SelectItem>
                  <SelectItem value="yearly">{t('admin.lastYear')}</SelectItem>
                  <SelectItem value="allTime">{t('admin.allTime')}</SelectItem>
                  <SelectItem value="custom">{t('admin.customRange')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {revenuePeriod === 'custom' && (
              <>
                <div className="space-y-2">
                  <Label>{t('admin.startDate')}</Label>
                  <Button variant="outline" className="w-40 justify-start text-left font-normal"
                    onClick={() => { setShowDatePicker(true); setDatePickerMode('start'); }}>
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {revenueStartDate ? new Date(revenueStartDate).toLocaleDateString() : t('admin.selectDateBtn')}
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.endDate')}</Label>
                  <Button variant="outline" className="w-40 justify-start text-left font-normal"
                    onClick={() => { setShowDatePicker(true); setDatePickerMode('end'); }}>
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {revenueEndDate ? new Date(revenueEndDate).toLocaleDateString() : t('admin.selectDateBtn')}
                  </Button>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>{t('admin.filterByTherapist')}</Label>
              <Select value={revenueTherapistFilter} onValueChange={setRevenueTherapistFilter}>
                <SelectTrigger className="w-48"><SelectValue placeholder={t('admin.allTherapists')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('admin.allTherapists')}</SelectItem>
                  {therapists.map((th) => (
                    <SelectItem key={th.id} value={th.id}>{th.name}</SelectItem>
                  ))}
                  <SelectItem value="Unassigned">{t('common.unassigned')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(revenueTherapistFilter !== 'all' || revenuePeriod === 'custom') && (
              <Button variant="ghost" size="sm" onClick={() => {
                setRevenueTherapistFilter('all'); setRevenuePeriod('weekly');
                setRevenueStartDate(null); setRevenueEndDate(null);
              }}>{t('common.clearFilters')}</Button>
            )}

            {/* Export Buttons */}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-revenue-csv">
                <Download className="w-4 h-4 mr-1" /> CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-revenue-pdf">
                <Download className="w-4 h-4 mr-1" /> PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Revenue Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-700">{t('admin.totalRevenue')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-800">฿{currentRevenueData.total.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-blue-700">{t('admin.completedBookings')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-800">{currentRevenueData.count}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-purple-700">{t('admin.avgPerBooking')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-800">฿{currentRevenueData.avgPerBooking.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue by Therapist */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('admin.revenueByTherapist')}</CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(currentRevenueData.byTherapist).length === 0 ? (
            <p className="text-gray-500 text-center py-4">{t('admin.noCompletedBookings')}</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(currentRevenueData.byTherapist)
                .sort((a, b) => b[1].revenue - a[1].revenue)
                .map(([name, data]) => (
                  <div key={name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium">{name}</span>
                      <span className="text-sm text-gray-500 ml-2">({data.count} {t('common.bookings')})</span>
                    </div>
                    <span className="font-bold text-green-600">฿{data.revenue.toLocaleString()}</span>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revenue by Service */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('admin.revenueByService')}</CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(currentRevenueData.byService).length === 0 ? (
            <p className="text-gray-500 text-center py-4">{t('admin.noCompletedBookings')}</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(currentRevenueData.byService)
                .sort((a, b) => b[1].revenue - a[1].revenue)
                .map(([service, data]) => (
                  <div key={service} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium">{service}</span>
                      <span className="text-sm text-gray-500 ml-2">({data.count} {t('common.bookings')})</span>
                    </div>
                    <span className="font-bold text-green-600">฿{data.revenue.toLocaleString()}</span>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
